/// <reference types="vite/client" />
import * as z from 'zod';

/**
 * Runtime validation of the app environment. The app fails fast at boot if a
 * required variable is missing or malformed, instead of failing deep in a
 * request. Zod is the single source of truth — see CLAUDE.md "Conventions".
 *
 * Source: `import.meta.env` overlaid by container-generated `window.__ENV__`.
 * Deployment bundles require runtime values; local builds use env files.
 * Values used by browser code are
 * public by definition; never put credentials or other secrets in VITE_*.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.url(),
  /** Base for uploaded file paths. Unset = VITE_API_BASE_URL (see merchant-form). */
  VITE_API_FILES_URL: z.url().optional(),
  /**
   * Merchant employee service base. The staff roster lives on the Eats test
   * gateway even though most Eats domain resources use VITE_API_BASE_URL.
   */
  VITE_MERCHANT_EMPLOYEE_API_BASE_URL: z
    .url()
    .default('https://eats.test.api.noor.uz/api/v1'),
  VITE_ENABLE_MSW: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
  /**
   * Eats sub-app launcher gate (merchant-panel welcome picker + header
   * switcher). `true` opens Eats as navigable; `false` teases it as "soon".
   * Defaults to open — the only deployed contour is dev, so set this to
   * `false` in the real-production env when it is authored.
   */
  VITE_EATS_ENABLED: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
  /**
   * Tags Eats with a "Soon" badge while leaving it navigable (merchant-panel
   * launcher). Independent of `VITE_EATS_ENABLED`: that gate decides whether
   * Eats can be opened at all; this one only adds the tag. Production runs
   * `VITE_EATS_ENABLED=true` + `VITE_EATS_SOON_BADGE=true` — open, but flagged
   * as not yet generally launched. Defaults to off.
   */
  VITE_EATS_SOON_BADGE: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
  VITE_APP_NAME: z.string().min(1).default('Delivery Admin'),
  /**
   * Legacy build variable retained for compatibility. The Maps SDK now uses
   * only keys returned by /geocode/map-keys.
   */
  VITE_YANDEX_MAPS_API_KEY: z.string().default(''),
  /**
   * Yandex Geocoder HTTP API key ("API Геокодера", `geocode-maps.yandex.ru`) —
   * a SEPARATE Yandex product from the JS API above, with its own key and
   * quota. Also the fallback for the Places key below, so a single-key setup
   * keeps working. Wired into the SDK via
   * `ymaps3.getDefaultConfig().setApikeys(...)` in the ymaps provider. Empty
   * keeps the JS-key fallback, which the HTTP backends answer with 403
   * (address search degrades; the map itself still works).
   */
  VITE_YANDEX_GEOCODER_API_KEY: z.string().default(''),
  /**
   * Yandex Places / Organization Search HTTP API key ("API Поиска по
   * организациям", `search-maps.yandex.ru/v1/`) — again its own Yandex
   * product, key and quota, distinct from both the JS API key and the Geocoder
   * key above.
   *
   * This is the key `ymaps3.search()` signs its requests with, so it backs the
   * two places the merchant panel resolves an address: the address
   * autocomplete in `LegacyAddressSearch`, and the click / marker-drag reverse
   * geocode on the order-create map (`search({text: "lon, lat", sco:
   * longlat})`). Wrong key there is a 403 on every lookup while the map itself
   * renders fine, which reads as "search is broken" rather than "wrong key".
   *
   * Empty falls back to VITE_YANDEX_GEOCODER_API_KEY, then to the JS key.
   */
  VITE_YANDEX_PLACES_API_KEY: z.string().default(''),
  /**
   * Basic Authorization credential for `GET /geocode/map-keys` on the legacy
   * backend. Accepts either the full header value (`Basic abc...`) or just the
   * encoded credential (`abc...`). Required to enable maps; the Maps SDK never
   * falls back to a local Yandex env key.
   */
  VITE_MAP_KEYS_BASIC_AUTH: z.string().default(''),
  /**
   * Self-hosted OpenRouteService base for the road-snapped route polyline on
   * the order map (cpanel uses `routing.noor.uz`). Empty disables route drawing.
   */
  VITE_ROUTE_API_URL: z.string().default(''),
  /**
   * DEV-ONLY bearer token for the route service above.
   *
   * The route service authenticates with the merchant's own session token, so
   * in production nothing extra is needed. Locally there is no real session —
   * the mock one's token is not a JWT the service accepts — so the route
   * silently fails to draw, which reads as a broken feature.
   *
   * Supply a real token in `.env.local` (gitignored) to draw routes while
   * developing. It is deliberately NOT committed: the old panel embedded a
   * hardcoded JWT in its source for exactly this, and that credential is now
   * in every build it ever shipped.
   *
   * A note on the failure mode this papers over: the service answers 401
   * WITHOUT CORS headers, so an invalid token surfaces in the browser as
   * "Failed to fetch" rather than as a 401 — do not read that as a network or
   * CORS problem.
   */
  VITE_ROUTE_API_DEV_TOKEN: z.string().default(''),

  /**
   * Base URL for LEGACY mode — the toggleable reproduction of the old merchant
   * panel (see `entities/legacy-mode`). The old panel talks to a DIFFERENT
   * backend than `VITE_API_BASE_URL`: a different host, unenveloped responses,
   * and its own endpoint surface. It is therefore its own variable rather than
   * a path on the main base, and it is read only by the legacy HTTP client.
   * Dev points at `tback.noor.uz`, production at `back.noor.uz`.
   */
  VITE_LEGACY_API_BASE_URL: z.url().default('https://tback.noor.uz/api/v1'),

  /**
   * WebSocket endpoint for LEGACY mode. Same backend as
   * `VITE_LEGACY_API_BASE_URL` (hence its own variable, not `VITE_SOCKET_URL`,
   * which points at the new API): the old panel authenticates over the socket
   * with the legacy access token and receives `{"type":"refresh"}` pushes that
   * drive the orders list. Dev is `tback.noor.uz`, production `back.noor.uz`.
   */
  VITE_LEGACY_SOCKET_URL: z.string().default('wss://tback.noor.uz/ws'),

  /**
   * Base URL for WebSocket connections and every HTTP request under the `/ws/`
   * path prefix. Use a WebSocket scheme (`wss://` in production, `ws://` for
   * local non-TLS development); the shared HTTP client converts it to the
   * equivalent `https://` or `http://` scheme for `/ws/` API requests. The base
   * carries no trailing slash or `/ws` — that prefix belongs to each request.
   */
  VITE_WEBSOCKET_API_URL: z.string().default('wss://core.test.api.noor.uz'),

  /**
   * Whether MSW may mock the LEGACY backend — separate from
   * `VITE_ENABLE_MSW`, and OFF by default.
   *
   * `VITE_ENABLE_MSW` is a gap-filler for screens the new merchant API does
   * not serve yet; legacy mode is the opposite case — `tback.noor.uz` is a
   * REAL, reachable backend with real merchant sessions. Keying the legacy
   * handlers off the shared flag made them shadow that backend for anyone
   * running the default `.env` (which enables MSW for the gap screens), so the
   * orders list rendered 86 seeded rows over whatever the live endpoint
   * actually returned — indistinguishable from a broken page.
   *
   * Set to `true` only to develop the legacy screens WITHOUT a session for
   * that host. Both flags must be on for the legacy handlers to install.
   */
  VITE_ENABLE_LEGACY_MSW: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),

  /* ──────────────────────────────────────────────────────────────────────
   * Optional service configuration carried over from the legacy cpanel env.
   * Each service reads only the values it needs; keeping them lenient lets
   * deployments omit integrations that are not enabled for their contour.
   * ────────────────────────────────────────────────────────────────────── */
  /**
   * Auth-service host (cpanel `VITE_BASE_API_URL_AUTH`). Login
   * (`POST /api/v1/auth/user`) and token refresh
   * (`POST /api/v1/auth/refresh`) go here — never the main gateway.
   */
  VITE_AUTH_API_URL: z.url().default('https://core.test.api.noor.uz'),
  /**
   * Core merchant-profile host (cpanel `VITE_CORE_API_URL`). The admin
   * merchant surface (`/api/v1/admin/merchants`, employees, integration,
   * uploads presign) lives here, not on the main gateway.
   */
  VITE_CORE_API_URL: z.url().default('https://core.test.api.noor.uz'),
  /**
   * Eats host (same shape as `VITE_CORE_API_URL`, `eats.` prefix instead of
   * `core.`). Serves the Еда section resources (restaurant/product data,
   * banners, categories, uploads presign) and the merchants list while the
   * Eats vertical is selected.
   */
  VITE_EATS_API_URL: z.url().default('https://eats.test.api.noor.uz'),
  /**
   * Delivery host (`delivery.` prefix, like `VITE_EATS_API_URL`). Serves the
   * search-service admin settings (delivery settings, ETA settings,
   * multiorder parameters) under `/api/v1/admin/…`.
   */
  VITE_DELIVERY_API_URL: z.url().default('https://delivery.test.api.noor.uz'),
  /**
   * Core S3 public base (cpanel `VITE_CORE_S3_BASE_URL`). Change-request
   * image payloads carry bare storage keys (`restaurant/<uuid>`) instead of
   * full URLs — the UI joins them onto this base. Overridable per
   * environment; the default targets the dev bucket.
   */
  VITE_CORE_S3_BASE_URL: z.url().default('https://s3.test.api.noor.uz/core'),
  /**
   * Pharma host (cpanel `VITE_PHARMA_API_URL`, falling back to the test host
   * exactly like the old client did). Serves `/api/v1/companies/*`.
   */
  VITE_PHARMA_API_URL: z.url().default('https://pharma.test.api.noor.uz'),
  /** Market service API base (separate backend). */
  VITE_MARKET_API_URL: z.string().default(''),
  /** File API base (uploads / served assets). */
  VITE_FILE_API_URL: z.string().default(''),
  /**
   * Centrifugo websocket endpoint for Eats live updates (NOT the legacy
   * `VITE_LEGACY_SOCKET_URL`, and not the unused cpanel `VITE_SOCKET_URL`
   * below). Same gateway host as `VITE_API_BASE_URL`, `/connection/websocket`
   * path: the client authenticates with its access token and subscribes to the
   * channels `GET merchants/me/realtime` returns. Dev is `test.api.noor.uz`,
   * production `api.noor.uz`.
   */
  VITE_CENTRIFUGO_URL: z.string().default('wss://test.api.noor.uz/connection/websocket'),
  /** WebSocket endpoint for live order/chat updates. */
  VITE_SOCKET_URL: z.string().default(''),
  /** Sentry DSN for error reporting (empty disables Sentry). */
  VITE_SENTRY_DSN: z.string().default(''),
  /** Syncfusion community license key (empty = unlicensed/community). */
  VITE_SYNCFUSION_LICENSE_KEY: z.string().default(''),
  /** Firebase Cloud Messaging config (push notifications). */
  VITE_FIREBASE_API_KEY: z.string().default(''),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().default(''),
  VITE_FIREBASE_PROJECT_ID: z.string().default(''),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().default(''),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().default(''),
  VITE_FIREBASE_APP_ID: z.string().default(''),
  VITE_FIREBASE_MEASUREMENT_ID: z.string().default(''),
  VITE_FIREBASE_VAPID_KEY: z.string().default(''),
});

function canParseUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export function withRuntimeEnv(
  buildEnv: Record<string, unknown>,
  injected: unknown,
): Record<string, unknown> {
  const runtimeEnv: Record<string, string> = {};
  if (injected && typeof injected === 'object' && !Array.isArray(injected)) {
    for (const [key, value] of Object.entries(injected)) {
      if (key.startsWith('VITE_') && typeof value === 'string') runtimeEnv[key] = value;
    }
  }

  if (buildEnv.VITE_RUNTIME_ENV === 'true' || buildEnv.VITE_RUNTIME_ENV === 'cpanel') {
    // Require runtime routing values instead of silently using test defaults.
    const commonRequired = [
      'VITE_API_BASE_URL',
      'VITE_AUTH_API_URL',
      'VITE_LEGACY_API_BASE_URL',
      'VITE_FILE_API_URL',
      'VITE_ROUTE_API_URL',
      ...(buildEnv.VITE_RUNTIME_ENV === 'true' ? ['VITE_MAP_KEYS_BASIC_AUTH'] : []),
    ];
    const cpanel = buildEnv.VITE_RUNTIME_ENV === 'cpanel';
    const required = [
      ...commonRequired,
      ...(cpanel
        ? ['VITE_CORE_API_URL', 'VITE_EATS_API_URL', 'VITE_PHARMA_API_URL']
        : ['VITE_MERCHANT_EMPLOYEE_API_BASE_URL', 'VITE_LEGACY_SOCKET_URL']),
    ];
    const missing = required.filter((key) => !runtimeEnv[key]?.trim());
    if (missing.length) {
      throw new Error(`Missing runtime environment variables: ${missing.join(', ')}`);
    }
    const apiUrl = runtimeEnv.VITE_API_BASE_URL;
    if (
      apiUrl &&
      canParseUrl(apiUrl) &&
      ['api.noor.uz', 'back.noor.uz'].includes(new URL(apiUrl).hostname)
    ) {
      const productionHosts = {
        VITE_AUTH_API_URL: 'core.api.noor.uz',
        VITE_FILE_API_URL: 'back.noor.uz',
        VITE_ROUTE_API_URL: 'routing.api.noor.uz',
        ...(cpanel
          ? {
              VITE_CORE_API_URL: 'core.api.noor.uz',
              VITE_EATS_API_URL: 'eats.api.noor.uz',
              VITE_PHARMA_API_URL: 'pharma.api.noor.uz',
              ...(runtimeEnv.VITE_DELIVERY_API_URL
                ? { VITE_DELIVERY_API_URL: 'delivery.api.noor.uz' }
                : {}),
              ...(runtimeEnv.VITE_CORE_S3_BASE_URL
                ? { VITE_CORE_S3_BASE_URL: 's3.api.noor.uz' }
                : {}),
            }
          : {
              VITE_MERCHANT_EMPLOYEE_API_BASE_URL: 'eats.api.noor.uz',
              VITE_LEGACY_SOCKET_URL: 'back.noor.uz',
              ...(runtimeEnv.VITE_CENTRIFUGO_URL
                ? { VITE_CENTRIFUGO_URL: 'api.noor.uz' }
                : {}),
            }),
      };
      for (const [key, hostname] of Object.entries(productionHosts)) {
        const value = runtimeEnv[key];
        if (!value || !canParseUrl(value) || new URL(value).hostname !== hostname) {
          throw new Error(`${key} must target ${hostname} for production.`);
        }
      }
    }
    return { VITE_ENABLE_MSW: 'false', VITE_ENABLE_LEGACY_MSW: 'false', ...runtimeEnv };
  }

  return { ...buildEnv, ...runtimeEnv };
}

function parseEnv(): z.infer<typeof envSchema> {
  const parsed = envSchema.safeParse(
    withRuntimeEnv(import.meta.env, (globalThis as { __ENV__?: unknown }).__ENV__),
  );

  if (!parsed.success) {
    // Surface a readable error at boot rather than a cascade of undefined reads.
    const issues = z.flattenError(parsed.error);
    console.error('❌ Invalid environment variables:', issues.fieldErrors);
    throw new Error('Invalid environment variables. See console for details.');
  }

  return parsed.data;
}

export const env = parseEnv();
export type Env = typeof env;
